import { makeStyles } from "tss-react/mui";

import { Box, Theme } from "@mui/material";
// The one place the app draws a filter tag; see "Pills are for tags only" in .claude/conventions/ui.md.
// eslint-disable-next-line no-restricted-imports
import Chip from "@mui/material/Chip";

const useStyles = makeStyles()((theme: Theme) => ({
    row: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: theme.spacing(1),
    },
}));

type FilterTagOption<T extends string> = {
    value: T;
    label: string;
};

type Props<T extends string> = {
    options: FilterTagOption<T>[];
    value: T;
    onChange: (value: T) => void;
    /**
     * Names the whole row for screen readers, e.g. "Filter modules by tag".
     */
    label: string;
};

/**
 * A row of rounded tags that narrows the list below it, with one tag selected at a time.
 */
export const FilterTagRow = <T extends string>({ options, value, onChange, label }: Props<T>) => {
    const { classes } = useStyles();

    // A lone tag filters nothing on any page, so the row only earns its space from two options up.
    if (options.length < 2) {
        return null;
    }

    return (
        <Box className={classes.row} role="group" aria-label={label}>
            {options.map((option) => (
                <Chip
                    key={option.value}
                    label={option.label}
                    color="primary"
                    size="small"
                    variant={option.value === value ? "filled" : "outlined"}
                    onClick={() => onChange(option.value)}
                    aria-pressed={option.value === value}
                />
            ))}
        </Box>
    );
};
